import trim from 'lodash/trim';
import { logException } from 'shared/lib/utils';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_SURE_API_BASE_URL || '';

/**
 * Sign in with user credentials.
 *
 * @param {Object} opts The options.
 * @param {String} opts.email The user's email address.
 * @param {String} opts.password The user's password.
 */
export function signIn({ email, password }) {
  return fetch(`${API_BASE_URL}/api/user/v2/auth/user-login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: trim(email),
      password,
    }),
  }).then(response => {
    if (response.status >= 400) {
      return response.json().then(errorResp => {
        const error = new Error(response.statusText);
        logException(error, {
          email,
          response: errorResp,
        });
        throw error;
      });
    } else {
      return response.json();
    }
  });
}

/**
 * Sign up with user credentials.
 *
 * @param {Object} opts The options.
 * @param {String} opts.email The user's email address.
 * @param {String} opts.password The user's password.
 * @param {String} opts.firstName The user's first name.
 * @param {String} opts.lastName The user's last name.
 */
export function signUp(opts) {
  const payload = {
    email: trim(opts.email),
    password: opts.password,
    first_name: trim(opts.firstName),
    last_name: trim(opts.lastName),
  };
  return axios.post(`${API_BASE_URL}/api/user/v2/accounts/register`, payload);
}

/**
 * Log in using a Facebook auth token.
 *
 * @param {String} fbToken The Facebook access token.
 */
export function logInWithFacebook(fbToken) {
  return fetch(`${API_BASE_URL}/api/user/v2/auth/facebook-login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ facebook_token: fbToken }),
  }).then(response => {
    if (response.status >= 400) {
      return response.json().then(errorResp => {
        const error = new Error(response.statusText);
        logException(error, {
          response: errorResp,
        });
        throw error;
      });
    } else {
      return response.json();
    }
  });
}

/**
 * Send a reset password email.
 *
 * @param {String} email The user's email address.
 */
export function sendResetPasswordEmail(email) {
  return fetch(
    `${API_BASE_URL}/api/user/v2/accounts/verification/reset-password/send`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: trim(email) }),
    }
  ).then(response => {
    if (response.status >= 400) {
      return response.json().then(errorResp => {
        const error = new Error(response.statusText);
        logException(error, {
          email: trim(email),
          response: errorResp,
        });
        throw error;
      });
    } else {
      return response;
    }
  });
}

/**
 * Submit a reset password change.
 *
 * @param {Object} opts The options.
 * @param {String} opts.token The reset password token.
 * @param {String} opts.password The new password.
 */
export function submitResetPassword({ token, password }) {
  return fetch(
    `${API_BASE_URL}/api/user/v2/accounts/verification/reset-password/change`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, password }),
    }
  ).then(response => {
    if (response.status >= 400) {
      return response.json().then(errorResp => {
        const error = new Error(response.statusText);
        logException(error, {
          response: errorResp,
        });
        throw error;
      });
    } else {
      return response;
    }
  });
}

/**
 * Verify an email address verification token.
 *
 * @param {String} token The email address verification token.
 */
export function verifyEmailAddressToken(token) {
  return fetch(
    `${API_BASE_URL}/api/user/v2/accounts/verification/email/verify`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    }
  ).then(response => {
    if (response.status >= 400) {
      return response.json().then(errorResp => {
        const error = new Error(response.statusText);
        logException(error, {
          response: errorResp,
        });
        throw error;
      });
    } else {
      return response;
    }
  });
}
